import type { ReactNode } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { LeadStatus, formatCurrency } from "../constants";
import { colors, shadows, typography, spacing } from "../../../../assets/styles/tokens";

interface AnalyticsMobileHighLevelProps {
  data: LeadsManagementData;
}

// Same shadow-token flattening used elsewhere on this module's mobile cards
// (e.g. LeadMobileCard) — a shadow token's $value is a list of layers, each
// turned into one "offsetX offsetY blur spread color" CSS shadow.
function buildShadow(shadowToken: any): string {
  return shadowToken.$value
    .map((layer: any) => `${layer.offsetX} ${layer.offsetY} ${layer.blur} ${layer.spread} ${layer.color}`)
    .join(", ");
}

// Same Card base as LeadMobileCard — mt/border/radius/boxShadow and the
// 8px CardContent padding, so the Analytics stat cards match the Leads
// tab's card styling exactly rather than approximating it.
function StatCard({ children }: { children: ReactNode }) {
  return (
    <Card
      sx={{
        mt: spacing.Spacing.xl.$value,
        borderRadius: spacing.Radius.md.$value,
        border: `1px solid ${colors.GrayLightMode[200].$value}`,
        boxShadow: buildShadow(shadows.Shadows.sm),
      }}
    >
      <CardContent sx={{ p: spacing.Spacing.md.$value, "&:last-child": { pb: spacing.Spacing.md.$value } }}>{children}</CardContent>
    </Card>
  );
}

function CardTitle({ children }: { children: ReactNode }) {
  return (
    <Typography
      sx={{
        fontFamily: typography.Font_Family.$value,
        fontWeight: typography.Font_weight.Medium.$value,
        fontSize: typography.Size.Text.xs.$value,
        lineHeight: typography.Line_height.text.xs.$value,
        letterSpacing: "0%",
        color: colors.GrayLightMode[700].$value,
      }}
    >
      {children}
    </Typography>
  );
}

function PercentBadge({ value, tone }: { value: string; tone: "success" | "warning" }) {
  const color = tone === "success" ? colors.Success[600].$value : colors.Warning[600].$value;
  const bg = tone === "success" ? colors.Success[50].$value : colors.Warning[50].$value;
  return (
    <Box
      sx={{
        px: spacing.Spacing.md.$value,
        py: spacing.Spacing.xxs.$value,
        borderRadius: spacing.Radius.xs.$value,
        fontFamily: typography.Font_Family.$value,
        fontWeight: typography.Font_weight.Semibold.$value,
        fontSize: typography.Size.Text.xs.$value,
        lineHeight: typography.Line_height.text.xs.$value,
        color,
        bgcolor: bg,
      }}
    >
      {value}
    </Box>
  );
}

function RegularSmSpan({ children }: { children: ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        fontFamily: typography.Font_Family.$value,
        fontWeight: typography.Font_weight.Regular.$value,
        fontSize: typography.Size.Text.sm.$value,
        lineHeight: typography.Line_height.text.sm.$value,
        letterSpacing: "0%",
        color: colors.GrayLightMode[600].$value,
      }}
    >
      {children}
    </Box>
  );
}

function BigValue({
  value,
  connector = "out of",
  count,
  unit,
}: {
  value: ReactNode;
  connector?: string;
  count: ReactNode;
  unit: string;
}) {
  return (
    <Typography>
      <Box
        component="span"
        sx={{
          fontFamily: typography.Font_Family.$value,
          fontWeight: typography.Font_weight.Semibold.$value,
          fontSize: typography.Size.Text.xl.$value,
          lineHeight: typography.Line_height.text.xl.$value,
          letterSpacing: "0%",
          color: colors.GrayLightMode[900].$value,
        }}
      >
        {value}
      </Box>{" "}
      <RegularSmSpan>{connector}</RegularSmSpan>{" "}
      <Box
        component="span"
        sx={{
          fontFamily: typography.Font_Family.$value,
          fontWeight: typography.Font_weight.Semibold.$value,
          fontSize: typography.Size.Text.sm.$value,
          lineHeight: typography.Line_height.text.sm.$value,
          letterSpacing: "0%",
          color: colors.GrayLightMode[900].$value,
        }}
      >
        {count}
      </Box>{" "}
      <RegularSmSpan>{unit}</RegularSmSpan>
    </Typography>
  );
}

function StackedStat({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: ReactNode;
  valueColor?: string;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <Typography
        sx={{
          fontFamily: typography.Font_Family.$value,
          fontSize: typography.Size.Text.sm.$value,
          color: colors.GrayLightMode[700].$value,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: typography.Font_Family.$value,
          fontWeight: typography.Font_weight.Semibold.$value,
          fontSize: typography.Size.Text.sm.$value,
          color: valueColor ?? colors.GrayLightMode[900].$value,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

// Matches the Figma's "3m 0sec" wording — kept local rather than reusing
// the shared formatDuration helper, which spells it "3m 0s" and is used
// as-is elsewhere in the desktop Analytics KPI cards.
function formatMobileDuration(seconds: number | null): string {
  if (seconds === null) return "—";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return minutes > 0 ? `${minutes}m ${remainingSeconds}sec` : `${remainingSeconds}sec`;
}

export default function AnalyticsMobileHighLevel({ data }: AnalyticsMobileHighLevelProps) {
  const { aggregations, totalFiltered } = data;

  const countFor = (status: LeadStatus) =>
    aggregations.byStatus.find((item) => item.status === status)?.count ?? 0;
  const reported = countFor("Success");
  const failed = countFor("Failure");
  const rejected = countFor("Rejected");
  // Same grouping as the Leads tab's "Internal Duplication" status tab.
  const internalDuplication = countFor("DuplicateIP") + countFor("DuplicateEmail");
  const brandDuplication = countFor("BrandDuplication");
  const reportedRate = totalFiltered ? Math.round((reported / totalFiltered) * 100) : 0;

  const { qualifiedByBrand, incomeFromBrand, avgLeadScore, avgFillTimeSeconds } = aggregations;
  const qualifiedOfReportedRate =
    qualifiedByBrand.percentOfSuccessLeads !== null ? Math.round(qualifiedByBrand.percentOfSuccessLeads) : 0;

  return (
    <Box sx={{ pb: spacing.Spacing.md.$value }}>
      <StatCard>
        <CardTitle>Leads</CardTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: spacing.Spacing.xs.$value }}>
          <BigValue value={reported} connector="qualified out of" count={totalFiltered} unit="Total Leads" />
          <PercentBadge value={`${reportedRate}%`} tone="success" />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: spacing.Spacing.xs.$value, mt: spacing.Spacing.xs.$value }}>
          <StackedStat label="Failed" value={failed} />
          <StackedStat label="Rejected" value={rejected} />
          <StackedStat label="Internal Duplication" value={internalDuplication} />
          <StackedStat label="Brand Duplication" value={brandDuplication} />
        </Box>
      </StatCard>

      <StatCard>
        <CardTitle>Monetisation</CardTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: spacing.Spacing.xs.$value }}>
          <BigValue value={qualifiedByBrand.count} connector="Paid out of" count={reported} unit="Reported Leads" />
          <PercentBadge value={`${qualifiedOfReportedRate}%`} tone="warning" />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: spacing.Spacing.xs.$value, mt: spacing.Spacing.xs.$value }}>
          <StackedStat label="Total Income from Brands" value={formatCurrency(incomeFromBrand.total)} />
          <StackedStat label="Avg. Per Reported Lead" value={formatCurrency(incomeFromBrand.avgPerSuccessLead)} />
          <StackedStat label="Avg. Fill Time" value={formatMobileDuration(avgFillTimeSeconds.averageSeconds)} />
          <StackedStat
            label="Avg. Lead Score"
            value={
              <>
                <Box
                  component="span"
                  sx={{
                    fontFamily: typography.Font_Family.$value,
                    fontWeight: typography.Font_weight.Semibold.$value,
                    fontSize: typography.Size.Text.sm.$value,
                    lineHeight: typography.Line_height.text.sm.$value,
                    letterSpacing: "0%",
                    color: colors.Success[600].$value,
                  }}
                >
                  {avgLeadScore.average !== null ? avgLeadScore.average.toFixed(1) : "—"}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontFamily: typography.Font_Family.$value,
                    fontWeight: typography.Font_weight.Regular.$value,
                    fontSize: typography.Size.Text.xs.$value,
                    lineHeight: typography.Line_height.text.xs.$value,
                    letterSpacing: "0%",
                    verticalAlign: "middle",
                    color: colors.GrayLightMode[900].$value,
                  }}
                >
                  /10
                </Box>
              </>
            }
          />
        </Box>
      </StatCard>
    </Box>
  );
}
